import isEqual from 'lodash/isEqual';
import type { ByDay, Frequency, RecurrenceRule } from '@atlassian/jira-ical/src/types.tsx';
import { Days, Frequencies } from '@atlassian/jira-ical/src/constants.tsx';
import type { MessageDescriptor } from '@atlassian/jira-intl';
import type { RecurFrequency } from './fields/RecurFrequencyField.tsx';
import messages from './messages.tsx';

export const mapFrequencyToRRule = (
	timeframe: RecurFrequency,
	recurWeeklyConfig: ByDay[],
):
	| Pick<RecurrenceRule, 'frequency' | 'byDay' | 'interval'>
	| Pick<RecurrenceRule, 'frequency' | 'interval'> => {
	switch (timeframe) {
		case 'DAILY':
			return { frequency: Frequencies.DAILY, interval: 1 };
		case 'WEEKDAY':
			return {
				frequency: Frequencies.DAILY,
				byDay: [Days.MO, Days.TU, Days.WE, Days.TH, Days.FR],
				interval: 1,
			};
		case 'WEEKLY':
			return { frequency: Frequencies.WEEKLY, byDay: recurWeeklyConfig, interval: 1 };
		case 'FORTNIGHTLY':
			// There is no native FORTNIGHTLY frequency for ical, so we need to combine WEEKLY with interval 2
			return { frequency: Frequencies.WEEKLY, byDay: recurWeeklyConfig, interval: 2 };
		default:
			throw new Error(`Unknown timeframe: ${timeframe}`);
	}
};

export const mapFrequencyToSmartOffsetValue = (timeframe: RecurFrequency) => {
	switch (timeframe) {
		case 'DAILY':
			return '{{now.plusDays(1)}}';
		case 'WEEKDAY':
			return '{{now.plusBusinessDays(1)}}';
		case 'WEEKLY':
			return '{{now.plusWeeks(1)}}';
		case 'FORTNIGHTLY':
			return '{{now.plusWeeks(2)}}';
		default:
			throw new Error(`Could not calculate due date smart offset value: ${timeframe}`);
	}
};

export const getRecurMenuItemLabel = (
	{
		frequency,
		interval,
		byDay,
	}: {
		frequency: Frequency | undefined;
		interval: number | undefined;
		byDay: ByDay[] | undefined;
	},
	isRecurOnComplete = false,
): MessageDescriptor => {
	if (isRecurOnComplete) {
		return messages.recursOnComplete;
	}

	// Every business day (Monday to Friday)
	if (
		frequency === Frequencies.DAILY &&
		isEqual(byDay, [Days.MO, Days.TU, Days.WE, Days.TH, Days.FR])
	) {
		return messages.recursEveryWeekday;
	}
	// Daily
	if (frequency === Frequencies.DAILY) {
		return messages.recursDaily;
	}
	// Fortnightly
	if (frequency === Frequencies.WEEKLY && interval === 2) {
		return messages.recursFortnightly;
	}
	// Weekly
	if (frequency === Frequencies.WEEKLY) {
		return messages.recursWeekly;
	}

	return messages.setToRecur;
};
